import axios from "axios";
import toast from "react-hot-toast";

// eslint-disable-next-line no-undef
const baseURL = process.env["REACT_APP_BASE_URL"];

if (!baseURL) {
  console.error("REACT_APP_BASE_URL is not defined in environment variables");
}

// Custom error class for better error handling
export class APIError extends Error {
  constructor(message, status, originalError) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.originalError = originalError;
  }
}

export const getAxios = () => {
  let axiosInstance;

  const accessToken = window.localStorage.getItem("accessToken");
  const uid = window.localStorage.getItem("uid");
  const client = window.localStorage.getItem("client");

  const headers =
    accessToken && uid && client
      ? {
          uid: uid,
          "access-token": accessToken,
          client: client,
          "Content-Type": "application/json",
          Accept: "application/json",
        }
      : {
          Accept: "application/json",
        };

  axiosInstance = axios.create({
    baseURL,
    headers,
    timeout: 30000,
  });

  axiosInstance.interceptors.response.use(
    function (response) {
      return Promise.resolve(response);
    },
    function (error) {
      // Handle different error types
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.errors?.[0] || error.response.data?.error || error.message;

        switch (status) {
          case 401:
            // Unauthorized - redirect to login if not already there
            if (!window.location.pathname.includes('/login')) {
              toast.error("Your session has expired. Please log in again.");
              // Clear auth tokens
              window.localStorage.removeItem("accessToken");
              window.localStorage.removeItem("uid");
              window.localStorage.removeItem("client");
              // Redirect to login after a short delay
              setTimeout(() => {
                window.location.href = "/login";
              }, 1500);
            }
            break;
          case 403:
            toast.error("You don't have permission to access this resource.");
            break;
          case 404:
            // Don't show toast for 404s - let individual pages handle them
            console.warn(`Resource not found: ${error.config?.url}`);
            break;
          case 422:
            // Validation errors - let the form handle these
            console.warn("Validation error:", message);
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            toast.error("Server error. Please try again later.");
            break;
          default:
            if (status >= 400) {
              toast.error(message || "An error occurred. Please try again.");
            }
        }

        return Promise.reject(new APIError(message, status, error));
      } else if (error.request) {
        // Request made but no response received
        toast.error("Network error. Please check your internet connection.");
        return Promise.reject(new APIError("Network error", 0, error));
      } else {
        // Something else happened
        toast.error("An unexpected error occurred.");
        return Promise.reject(error);
      }
    }
  );

  return axiosInstance;
};

export const get = (uri, config = {}) => {
  return new Promise((resolve, reject) => {
    getAxios()
      .get(uri, config)
      .then(res => {
        resolve(res);
      })
      .catch(err => {
        reject(err);
      });
  });
};

export const post = (uri, data) => {
  return new Promise((resolve, reject) => {
    getAxios()
      .post(uri, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const put = (uri, data) => {
  return new Promise((resolve, reject) => {
    getAxios()
      .put(uri, data)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });
};

export const deleteAPI = uri => {
  return new Promise((resolve, reject) => {
    getAxios()
      .delete(uri)
      .then(res => {
        resolve(res);
      })
      .catch(error => {
        reject(error);
      });
  });
};
